<template>
	<div class="modal-body" ref="modalBody">
		<div class="message-list" data-testid="message-list">
			<div
				v-for="message in messages"
				:key="message.id"
				class="border-bottom py-1"
				:data-testid="message.id"
			>
				<p class="h5 mb-1">
					<strong>{{
						message.role === 'assistant' ? 'L&bull;AI&bull;C' : userData.firstName
					}}</strong>
				</p>
				<div class="small rendered" v-html="message.renderedContent"></div>
				<LaicActions
					v-if="!message.id.startsWith('temp-')"
					:message="message"
					:user-data="userData"
					@action-event="handleActionEvent"
				/>
			</div>
			<div v-if="isStreaming" class="border-bottom py-1">
				<p class="h5 mb-1">
					<strong>L&bull;AI&bull;C</strong>
				</p>
				<p v-if="!streamingContent" class="small mb-0">Thinking&hellip;</p>
				<div v-else class="small rendered" v-html="renderMarkdown(streamingContent)"></div>
			</div>
		</div>
	</div>
	<div class="modal-footer">
		<form class="flex-grow-1" data-testid="form-messages" @submit.prevent="sendMessage()">
			<div class="input-group">
				<label for="laic-message-input" class="visually-hidden">Type your message</label>
				<input
					id="laic-message-input"
					class="form-control"
					data-testid="input-message"
					placeholder="Type your message..."
					type="text"
					v-model="messageInput"
				/>
				<button
					aria-label="Send Message"
					class="btn btn-dark px-2"
					data-testid="btn-submit"
					:disabled="isStreaming"
					type="submit"
				>
					<i class="bi bi-arrow-up"></i>
				</button>
			</div>
		</form>
	</div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { Marked } from 'marked';
import { nextTick, onBeforeMount, ref, type Ref, useTemplateRef } from 'vue';
import LaicActions from './LaicActions.vue';
import { aiService } from '@/services/aiService';
import { logError } from '@/util/logger';
import { parseSSEStream } from '@/util/sse';
import type { ActionEvent, Message, UserData } from 'Types/aemui/laic-chat/laicChat';

const props = defineProps<{
	userData: UserData;
}>();

const modalBodyEl = useTemplateRef('modalBody');
const scrollToBottom = async () => {
	await nextTick();
	modalBodyEl.value?.scrollTo(0, modalBodyEl.value?.scrollHeight);
};

const conversationId = ref('');
export interface MessageBubble extends Message {
	renderedContent: string;
}
const messages: Ref<MessageBubble[]> = ref([]);

onBeforeMount(async () => {
	try {
		const response = await aiService.init({
			agent: 'prospect',
			userId: props.userData.email,
			userData: props.userData
		});
		conversationId.value = response.id;
		const content = `Hi, I'm L•AI•C (pronounced "lay-see"), an AI assistant ready to answer your questions about membership, pricing, and policies. I can also set up a club tour for you.

This is an AI-powered chat and may make mistakes. See our [terms of use](https://www.lifetime.life/policy/terms-of-use.html) and [privacy policy](https://www.lifetime.life/policy/privacy-policy.html).

How can I help?`;

		messages.value.push({
			id: `temp-${dayjs().valueOf()}`,
			role: 'assistant',
			content,
			renderedContent: renderMarkdown(content),
			liked: null,
			timestamp: dayjs().format()
		});
	} catch {
		finalizeStreamMessage(`error-${dayjs().valueOf()}`, true);
	}
});

const messageInput = ref('');
const isStreaming = ref(false);
const streamingContent = ref('');

function finalizeStreamMessage(messageId?: string, isError = false) {
	if (!streamingContent.value && !isError) {
		return;
	}
	const content = isError ? 'Something went wrong. Please try again later' : streamingContent.value;
	const assistantMessage: MessageBubble = {
		id: messageId ?? `temp-${dayjs().valueOf()}`,
		content,
		renderedContent: renderMarkdown(content),
		role: 'assistant',
		liked: null,
		timestamp: dayjs().format()
	};
	messages.value.push(assistantMessage);
	streamingContent.value = '';
	scrollToBottom();
}

const sendMessage = async () => {
	if (!messageInput.value.trim()) {
		return;
	}

	const content = messageInput.value.trim();
	const userMessage: MessageBubble = {
		id: `temp-${dayjs().valueOf()}`,
		role: 'user',
		content,
		renderedContent: renderMarkdown(content),
		liked: null,
		timestamp: dayjs().format()
	};
	messages.value.push(userMessage);

	let assistantMessageId;
	try {
		isStreaming.value = true;
		const response = await aiService.postConversation({
			agent: 'prospect',
			conversationId: conversationId.value,
			message: messageInput.value.trim(),
			timestamp: dayjs().format(),
			userData: props.userData
		});
		messageInput.value = '';
		await scrollToBottom();

		for await (const event of parseSSEStream(response)) {
			// The only account of how the run ended: the stream closes the same way
			// whether the answer finished or was cut off.
			if (event.event === 'terminal') {
				if (event.outcome === 'error' || event.outcome === 'timeout') {
					logError('LAIC conversation did not finish', { event });
					throw new Error(`LAIC conversation ended: ${event.outcome}`);
				}
				break;
			}

			if (event.event !== 'delta') {
				continue;
			}

			switch (event.o) {
				case 'add':
					assistantMessageId = event.v.message.id;
					break;
				case 'append':
					streamingContent.value += event.v;
					await scrollToBottom();
					break;
			}
		}
		finalizeStreamMessage(assistantMessageId);
	} catch (e) {
		if ((e as Error).name !== 'AbortError') {
			finalizeStreamMessage(assistantMessageId, true);
		}
	} finally {
		isStreaming.value = false;
	}
};

const marked = new Marked({
	gfm: true,
	breaks: true
});

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
	if (node instanceof HTMLAnchorElement) {
		node.setAttribute('rel', 'noopener noreferrer');
		node.setAttribute('target', '_blank');
	}
});

const renderMarkdown = (content: string): string => {
	const raw = marked.parse(content) as string;
	return DOMPurify.sanitize(raw, {
		USE_PROFILES: { html: true },
		ALLOW_UNKNOWN_PROTOCOLS: false
	});
};

const handleActionEvent = (eventData: ActionEvent) => {
	const message = messages.value.find((m) => m.id === eventData.messageId);
	if (message) {
		message.liked = eventData.liked;
	}
};

defineExpose({
	conversationId,
	messages,
	messageInput,

	handleActionEvent
});
</script>

<style lang="scss" scoped>
.message-list .border-bottom:last-child {
	border-bottom-width: 0 !important;
}

:deep(.rendered p:last-child) {
	margin-bottom: 0.5rem;
}

.message-actions button {
	border: 0;
	background: transparent;
	font-size: 12px;

	&:disabled {
		color: var(--kpl-dark);
	}
}

.modal-footer {
	.form-control {
		font-size: 0.875rem;
	}
}
</style>
